import { useContext, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ReactEditor, useSlate } from 'slate-react';
import { useCoverageScope } from 'app/components/editor/coverage/CoverageScopeContext';
import { useEditorMode } from 'app/state/contexts/EditorContext';
import { TagContext } from 'app/state/contexts/TagContext';
import { SidebarContext } from 'app/state/contexts/SidebarContext';
import { getBlockTagBindings, hasBlockTags } from 'app/slate/extensions/tag/types/block';
import { BlockTagChrome } from 'app/slate/extensions/tag/components/BlockTagChrome';
import { useBlockTagColors, textBlockTagRegionStyle } from 'app/slate/extensions/tag/utils/useBlockTagColors';
export const StyledElement = ({ element, children }) => {
  const formatting = element.formatting;
  const elementRevisionId = useMemo(() => element.revisionId || uuidv4(), [element.revisionId]);
  const { scopeUuids, missingUuids, anchorsVisible } = useCoverageScope();
  const editor = useSlate();
  const { isTaggingMode, taggingSubMode } = useEditorMode();
  const { setBlockSelection, setClickedTagIds } = useContext(TagContext) ?? {};
  const { setIsSidebarOpen } = useContext(SidebarContext) ?? {};

  const bindings = element.type === 'paragraph' ? getBlockTagBindings(element) : [];
  const isBlockTagged = isTaggingMode && bindings.length > 0 && hasBlockTags(element);
  const blockTagColors = useBlockTagColors(bindings.length > 0 ? bindings : undefined);
  const showBlockChrome = isBlockTagged && blockTagColors && !blockTagColors.isHidden;
  const blockTagVisualStyle = showBlockChrome ? textBlockTagRegionStyle(blockTagColors) : null;

  const openTagInfo = () => {
    if (bindings.length === 0) return;
    setClickedTagIds?.(bindings.map((b) => b.tagId));
    setIsSidebarOpen?.(8);
  };

  const content = isBlockTagged ? (
    <span style={{ position: 'relative', display: 'inline', ...(blockTagVisualStyle ?? {}) }}>
      {children}
      {showBlockChrome && <BlockTagChrome bindings={bindings} indicatorColor={blockTagColors.indicator} />}
    </span>
  ) : (
    children
  );

  const handleBlockClick = (e) => {
    if (!isTaggingMode || taggingSubMode === 'mark-coverage' || element.type !== 'paragraph') return;
    if (e?.target?.closest?.('.ixbrl-tag')) return;
    if (bindings.length > 0) {
      openTagInfo();
      return;
    }
    if (!element.uuid) return;
    try {
      const path = ReactEditor.findPath(editor, element);
      setBlockSelection?.({ kind: 'paragraph', path, elementId: element.uuid });
    } catch {
      /* element not attached to the editor tree yet — ignore */
    }
  };

  const metadata = {
    'data-element-super-id': element.uuid,
    'data-document-revision-id': elementRevisionId,
    'data-element-revision-id': `${element.uuid}_${elementRevisionId}`,
    'data-element-uuid': element.uuid,
    onClick: handleBlockClick,
  };

  const superId = `xrp-superid-${element.uuid}`;

  let coverageClass = '';
  if (element.uuid) {
    if (missingUuids.has(element.uuid)) coverageClass = 'coverage-missing';
    else if (scopeUuids.has(element.uuid)) coverageClass = 'coverage-scope';
  }

  const hasBlockChild = Array.isArray(element.children) && element.children.every((child) => !!child.type);
  const Tag = hasBlockChild ? 'div' : 'p';

  if (!formatting) {
    return (
      <Tag id={superId} className={coverageClass || undefined} {...metadata}>
        {content}
      </Tag>
    );
  }

  const customStyling = {};
  switch (formatting.alignment) {
    case 'align-center':
      customStyling['textAlign'] = 'center';
      break;
    case 'align-justify':
      customStyling['textAlign'] = 'justify';
      break;
    case 'align-right':
      customStyling['textAlign'] = 'right';
      break;
    default:
      break;
  }
  let cssArray = [];
  if (!!formatting.class) {
    cssArray.push(formatting.class);
  }

  if (formatting.indent) {
    cssArray.push('indent');
  }

  if (anchorsVisible && element.coverage && element.coverage.rule && element.coverage.rule.kind) {
    cssArray.push('coverage-anchor');
    cssArray.push(`coverage-anchor--${element.coverage.rule.kind}`);
  }

  if (coverageClass) cssArray.push(coverageClass);

  const css = cssArray.length === 0 ? '' : cssArray.join(' ');

  switch (formatting.format) {
    case 'h1':
      return (
        <h1 id={superId} style={customStyling} className={css} {...metadata}>
          {content}
        </h1>
      );
    case 'heading-1':
      return (
        <h1 id={superId} style={customStyling} className={css} {...metadata}>
          {content}
        </h1>
      );
    case 'heading-one':
      return (
        <h1 id={superId} style={customStyling} className={css} {...metadata}>
          {content}
        </h1>
      );
    case 'h2':
      return (
        <h2 id={superId} style={customStyling} className={css} {...metadata}>
          {content}
        </h2>
      );
    case 'heading-2':
      return (
        <h2 id={superId} style={customStyling} className={css} {...metadata}>
          {content}
        </h2>
      );
    case 'heading-two':
      return (
        <h2 id={superId} style={customStyling} className={css} {...metadata}>
          {content}
        </h2>
      );
    case 'h3':
      return (
        <h3 id={superId} style={customStyling} className={css} {...metadata}>
          {content}
        </h3>
      );
    case 'heading-3':
      return (
        <h3 id={superId} style={customStyling} className={css} {...metadata}>
          {content}
        </h3>
      );
    case 'heading-three':
      return (
        <h3 id={superId} style={customStyling} className={css} {...metadata}>
          {content}
        </h3>
      );
    case 'h4':
      return (
        <h4 id={superId} style={customStyling} className={css} {...metadata}>
          {content}
        </h4>
      );

    case 'h5':
      return (
        <h5 id={superId} style={customStyling} className={css} {...metadata}>
          {content}
        </h5>
      );

    case 'h6':
      return (
        <h6 id={superId} style={customStyling} className={css} {...metadata}>
          {content}
        </h6>
      );
    case 'blockquote':
      /* <blockquote> is not valid xhtml, use class instead */
      return (
        <div id={superId} style={customStyling} className={`${css} xrp-blockquote`} {...metadata}>
          {content}
        </div>
      );
    case 'paragraph':
      return (
        <p id={superId} style={customStyling} className={css} {...metadata}>
          {content}
        </p>
      );
    case 'p':
      return (
        <p id={superId} style={customStyling} className={css} {...metadata}>
          {content}
        </p>
      );
    default:
      return (
        <p id={superId} style={customStyling} className={css} {...metadata}>
          {content}
        </p>
      );
  }
};
